import {
  OverflowTooltip,
  ChipCounterList,
  AvatarGroup,
  type TestResultsTableData,
} from '../../../components';
import dateRenderer from '../../../components/cellRenderers/dateRenderer';
import statusFilterRenderer from '../../../components/cellRenderers/statusFilterRenderer';
import { statusValueRenderer } from '../../../components/cellRenderers/statusRenderer';
import { testNameValueRenderer } from '../../../components/cellRenderers/testNameRenderer';
import testTypeFilterRenderer from '../../../components/cellRenderers/testTypeFilterRenderer';
import { Column, ColumnType } from '../../../components/Table';
import { capitalize, distinctArray, isInSaaSMode } from '../../../utils/common';
import dateComparer from '../../../utils/comparers/dateComparer';
import { statusValueComparer } from '../../../utils/comparers/statusComparer';
import dateFilterValueMatcher from '../../../utils/comparers/dateFilterValueMatcher';
import testSubTypeRenderer from '../../../components/cellRenderers/testSubTypeRenderer';
import testSourceTypeRenderer from '../../../components/cellRenderers/testSourceTypeRenderer';
import { ColumnFilterOption } from '../../../components/Table/types';
import { getFilterOptionsOverride } from '../../../utils/graphqlFilters';

export const getTestResultsTableColumns = (
  tableData: TestResultsTableData[],
  filterOptionsOverride: Record<string, ColumnFilterOption[]> | null = null,
): Column<TestResultsTableData>[] => {
  const isSaaS = isInSaaSMode();

  const getFilterOptions = (
    columnId: string,
    defaultOptions?: ColumnFilterOption[],
  ) =>
    getFilterOptionsOverride(columnId, filterOptionsOverride, defaultOptions);

  const testTagsFilterOptions = distinctArray(
    tableData.map((item) => item.testTags || ['']).flat(),
  ).map((item) => ({ value: item, label: item }));

  const assetTagsFilterOptions = distinctArray(
    tableData.map((item) => item.assetTags || []).flat(),
  )
    .map((item) => ({ value: item, label: item }))
    .concat([{ value: '', label: '' }]);

  const testOwnersFilterOptions = distinctArray(
    tableData.map((item) => item.testOwners || []).flat(),
  )
    .map((item) => ({ value: item, label: item }))
    .concat([{ value: '', label: '' }]);

  const assetOwnersFilterOptions = distinctArray(
    tableData.map((item) => item.assetOwners || []).flat(),
  )
    .map((item) => ({ value: item, label: item }))
    .concat([{ value: '', label: '' }]);

  const baseColumns: Column<TestResultsTableData>[] = [
    {
      id: 'databaseName',
      header: 'Database',
      width: 150,
      defaultUntoggled: true,
      filterOptionsOverride: getFilterOptions('databaseName'),
    },
    { id: 'schemaName', header: 'Schema', width: 140, defaultUntoggled: true },
    {
      id: 'tableName',
      header: 'Table name',
      width: 160,
      filterDropdownWidth: 350,
      filterOptionsOverride: getFilterOptions('tableName'),
    },
    {
      id: 'columnName',
      header: 'Column name',
      width: 180,
      filterDropdownWidth: 350,
      filterOptionsOverride: getFilterOptions('columnName'),
    },
    {
      id: 'testName',
      header: 'Test name',
      width: 180,
      renderer: ({ testName, testType, testSubType }) =>
        testNameValueRenderer(testName, testType, testSubType),
      nonToggleable: true,
      filterDropdownWidth: 400,
      filterOptionsOverride: getFilterOptions('testName'),
    },
    {
      id: 'testSubType',
      header: 'Test type',
      width: 140,
      renderer: ({ testSubType }) => testSubTypeRenderer(testSubType),
      filterRenderer: testTypeFilterRenderer,
      filterOptionsOverride: getFilterOptions('testSubType'),
    },
    {
      id: 'sourceType',
      header: 'Source',
      width: 120,
      renderer: ({ sourceType }) => testSourceTypeRenderer(sourceType),
      filterOptionsOverride: getFilterOptions('sourceType'),
    },
    {
      id: 'qualityDimension',
      width: 200,
      header: 'Quality dimension',
      defaultUntoggled: true,
      filterOptionsOverride: getFilterOptions('qualityDimension'),
    },
    {
      id: 'testTags',
      header: 'Test tags',
      width: 200,
      showSelectAll: false,
      defaultUntoggled: true,
      renderer: ({ testTags }) => <ChipCounterList items={testTags} />,
      filterOptionsOverride: getFilterOptions(
        'testTags',
        testTagsFilterOptions,
      ),
    },
    {
      id: 'testOwners',
      header: 'Test owners',
      width: 200,
      defaultUntoggled: true,
      emptyValueLabel: 'No owners',
      showSelectAll: false,
      filterOptionsOverride: getFilterOptions(
        'testOwners',
        testOwnersFilterOptions,
      ),
      renderer: ({ testOwners }) => <AvatarGroup items={testOwners} />,
    },
    {
      id: 'failedRows',
      header: 'Failed rows',
      width: 120,
      defaultUntoggled: true,
      filterable: false,
      compareFunc: (a, b) => (a.failedRows ?? 0) - (b.failedRows ?? 0),
      renderer: ({ failedRows }) => (
        <OverflowTooltip>
          {failedRows != null ? failedRows.toLocaleString() : '-'}
        </OverflowTooltip>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      width: 200,
      defaultUntoggled: true,
      hideColumnFilter: true,
      filterOperators: ['contains', 'notContains'],
      filterOptionsOverride: getFilterOptions('description'),
    },
    {
      id: 'latestRunTime',
      header: 'Last test run',
      width: 168,
      compareFunc: (a, b) => dateComparer(a.latestRunTime, b.latestRunTime),
      filterable: false,
      renderer: ({ latestRunTime }) => dateRenderer(latestRunTime),
    },
    {
      id: 'latestRunStatus',
      header: 'Status',
      renderer: ({ latestRunStatus }) => statusValueRenderer(latestRunStatus),
      compareFunc: (a, b) =>
        statusValueComparer(
          { status: a.latestRunStatus, date: a.latestRunTime },
          { status: b.latestRunStatus, date: b.latestRunTime },
        ),
      filterRenderer: statusFilterRenderer,
      nonToggleable: true,
      sticky: 'right',
      width: 120,
      filterOptionsOverride: getFilterOptions('latestRunStatus'),
    },
    {
      id: 'assetTags',
      header: 'Asset tags',
      width: 160,
      defaultUntoggled: true,
      emptyValueLabel: 'No tags',
      showSelectAll: false,
      filterOptionsOverride: getFilterOptions(
        'assetTags',
        assetTagsFilterOptions,
      ),
      renderer: ({ assetTags }) => <ChipCounterList items={assetTags} />,
    },
    {
      id: 'assetOwners',
      header: 'Asset owners',
      width: 200,
      defaultUntoggled: true,
      emptyValueLabel: 'No owners',
      showSelectAll: false,
      filterOptionsOverride: getFilterOptions(
        'assetOwners',
        assetOwnersFilterOptions,
      ),
      renderer: ({ assetOwners }) => <AvatarGroup items={assetOwners} />,
    },
    // Hidden column used only to provide a friendly label for the
    // `testCategory` filter chip when the user navigates here from the
    // dashboard summary cards. The filter is applied server-side and the
    // column itself is never rendered or selectable from the filter/toggle
    // menus.
    {
      id: 'testCategory',
      header: 'Test category',
      hideColumn: true,
    },
  ];

  if (isSaaS) {
    const assetSubscribersFilterOptions = distinctArray(
      tableData.map((item) => item.assetSubscribers || []).flat(),
    )
      .map((item) => ({ value: item, label: item }))
      .concat([{ value: '', label: '' }]);

    return [
      ...baseColumns,
      {
        id: 'assetLastDataModificationTime',
        header: 'Last table update',
        type: ColumnType.Date,
        width: 200,
        emptyValueLabel: 'No update time',
        renderer: ({ assetLastDataModificationTime }) => (
          <OverflowTooltip>
            {assetLastDataModificationTime
              ? dateRenderer(assetLastDataModificationTime, 'L LT')
              : '-'}
          </OverflowTooltip>
        ),
        compareFunc: (a, b) =>
          dateComparer(
            a.assetLastDataModificationTime,
            b.assetLastDataModificationTime,
          ),
        filterValueMatcher: ([from, to, emptyFlag], rowData) =>
          dateFilterValueMatcher(
            from,
            to,
            emptyFlag,
            rowData.assetLastDataModificationTime,
          ),
        filterOptionsOverride: getFilterOptions(
          'assetLastDataModificationTime',
        ),
      },
      {
        id: 'assetMaterialization',
        header: 'Asset materialization',
        defaultUntoggled: true,
        width: 220,
        renderer: ({ assetMaterialization }) => (
          <OverflowTooltip>
            {assetMaterialization ? capitalize(assetMaterialization) : '-'}
          </OverflowTooltip>
        ),
        filterOptionsOverride: getFilterOptions('assetMaterialization'),
      },
      {
        id: 'criticalAsset',
        header: 'Critical Asset',
        width: 180,
        defaultUntoggled: true,
        renderer: ({ criticalAsset }) => <>{criticalAsset ? 'Yes' : 'No'}</>,
        filterOptionsOverride: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
      },
      {
        id: 'assetName',
        header: 'Asset name',
        defaultUntoggled: true,
        width: 150,
        filterOptionsOverride: getFilterOptions('assetName'),
      },
      {
        id: 'assetPath',
        header: 'Asset path',
        defaultUntoggled: true,
        hideColumnFilter: true,
        filterOperators: ['contains', 'notContains'],
        filterOptionsOverride: getFilterOptions('assetPath'),
      },
      {
        id: 'assetSubscribers',
        header: 'Asset subscribers',
        defaultUntoggled: true,
        width: 200,
        emptyValueLabel: 'No subscribers',
        showSelectAll: false,
        filterOptionsOverride: getFilterOptions(
          'assetSubscribers',
          assetSubscribersFilterOptions,
        ),
        renderer: ({ assetSubscribers }) => (
          <AvatarGroup items={assetSubscribers} />
        ),
      },
    ];
  }

  return baseColumns;
};
